/**
 * 260917 `qr-pick-{code}` — 랜딩(QrLanding)이 고른 사업장을 개통 마법사(QrOnboard)·랜딩 재진입(fnRestorePlanState)으로
 * 넘기는 localStorage 이월의 **형태와 해석을 한 곳에** 둔다(계획 2 §9-4 · 판정 G-18). 의존 0 — `node -e` 스모크로 검증한다.
 *
 * 왜 검색 결과 행을 통째로 두지 않나(260904 형태) —
 *   가게 행은 공공데이터 디렉토리(tblPublicPlace)에 있고 `/api/qr/place/one?placeNo=` 로 언제든 다시 꺼낼 수 있다.
 *   저장소에 사본을 두면 DB 가 갱신돼도 옛 값(주소·좌표·업종)이 이긴다. 그래서 **키(placeNo)와 이름만** 남기고 복원 때 재조회한다.
 *   교회 행은 단건 조회 API 가 없어 사본을 남기되 **허용 필드만**(그 외 키는 버림) — 형태를 고정해 파서를 2벌로 만들지 않는다.
 *   구형 사본(카카오 kakaoId 행 통째)은 되찾을 길이 없다 → 읽는 자리에서 1회 소거(readPick).
 *
 * 저장 형태(storePick 이 canonical 로 줄여서 쓴다):
 *   { skipped: true }                                — 이름 없이 건너뜀
 *   { manual: true, name }                           — 검색에 없어 직접 적은 상호
 *   { placeNo, name }                                — 공공데이터 가게(placeNo 숫자 1~18자리)
 *   { churchKey, name, roadAddress, address, ... }   — 교회(CHURCH_PICK_FIELDS 만)
 *
 * parsePick 결과:
 *   { kind:'skipped' } | { kind:'manual', name } | { kind:'place', placeNo:Number, name } | { kind:'church', row } | null(소거 대상)
 */

// BE parsePlaceNo(QrController) 와 같은 판정 — 숫자 1~18자리만. 그 외(빈값·카카오 문자열 식별자)는 식별자가 아니다.
const PLACE_NO_RE = /^\d{1,18}$/;

/** `/api/qr/church/search` ChurchHit 중 이월에 남길 필드. hasCoord 같은 파생값은 버린다(복원 쪽이 latitude/longitude 로 다시 판다). */
export const CHURCH_PICK_FIELDS = [
  'churchKey', 'name', 'roadAddress', 'address', 'latitude', 'longitude', 'phone', 'serviceTime', 'denomination', 'introduce'
];

export const isPlaceNo = v => PLACE_NO_RE.test(String(v ?? ''));

export const pickKey = code => `qr-pick-${code}`;

const nameOf = obj => String(obj?.name ?? '').trim();

/**
 * 저장된(또는 저장하려는) 객체를 해석한다. 순수 함수 — 저장소를 건드리지 않는다.
 * 검색 결과 행(PublicPlaceHit·ChurchHit)을 그대로 넣어도 같은 규칙으로 해석되므로 쓰기·읽기가 한 파서를 공유한다.
 */
export const parsePick = raw => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (raw.skipped) return { kind: 'skipped' };
  if (raw.manual) return { kind: 'manual', name: nameOf(raw) };
  if (isPlaceNo(raw.placeNo)) return { kind: 'place', placeNo: Number(raw.placeNo), name: nameOf(raw) };
  if (typeof raw.churchKey === 'string' && raw.churchKey.trim()) {
    const row = {};
    for (const k of CHURCH_PICK_FIELDS) {
      if (raw[k] !== undefined && raw[k] !== null) row[k] = raw[k];
    }
    row.churchKey = raw.churchKey.trim();
    return { kind: 'church', row };
  }
  return null; // 구형 사본(kakaoId 행 통째)·정체불명 객체 — 되찾을 길이 없다
};

/** parsePick 결과를 저장 형태로 되돌린다. null 이면 저장할 게 없다(소거). */
export const toPickRecord = raw => {
  const pick = parsePick(raw);
  if (!pick) return null;
  if (pick.kind === 'skipped') return { skipped: true };
  if (pick.kind === 'manual') return { manual: true, name: pick.name };
  if (pick.kind === 'place') return { placeNo: pick.placeNo, name: pick.name };
  return pick.row;
};

export const clearPick = code => {
  try { window.localStorage.removeItem(pickKey(code)); } catch (_) { /* 저장소 차단 — 지울 것도 없다 */ }
};

/**
 * canonical 형태로 줄여서 쓴다. 검색 결과 행을 그대로 넘겨도 된다(가게 → {placeNo,name}, 교회 → 허용 필드).
 * 해석 불가 객체는 쓰지 않고 기존 키를 지운다 — 쓰레기를 남기면 다음 방문에 소거만 한 번 더 돈다.
 * ⚠️localStorage 다(sessionStorage 아님). 무료 트랙은 스토어를 거쳐 돌아오고, 인앱 브라우저는 문서를 통째로 다시 띄운다(260904).
 * @returns {boolean} 실제로 저장했나
 */
export const storePick = (code, obj) => {
  const rec = toPickRecord(obj);
  if (!rec) { clearPick(code); return false; }
  try {
    window.localStorage.setItem(pickKey(code), JSON.stringify(rec));
    return true;
  } catch (_) {
    return false; // 시크릿 모드 — 마법사가 다시 물으면 된다
  }
};

/**
 * 읽어서 해석한다. 해석 결과가 null(구형 사본·JSON 깨짐)이면 **그 자리에서 삭제**하고 null 을 준다(§9-4 1회 소거).
 * ⚠️해석 가능한 값은 여기서 지우지 않는다 — 지우는 자리는 개통 성공(QrOnboard fnClearDraft)·되돌리기(QrLanding fnResetPlan)뿐.
 *   새로고침·뒤로가기·인앱 재로드마다 이월이 증발하면 "내 매장 찾기가 또 나오는데?"(260904 유저)가 재발한다.
 */
export const readPick = code => {
  let raw = null;
  try { raw = window.localStorage.getItem(pickKey(code)); } catch (_) { return null; }
  if (!raw) return null;
  let obj = null;
  try { obj = JSON.parse(raw); } catch (_) { obj = null; }
  const pick = parsePick(obj);
  if (!pick) clearPick(code);
  return pick;
};
